/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
// @mui
import {
    Card,
    Typography,
    Box,
    Stack,
    useTheme,
    CircularProgress,
    alpha,
    Tooltip,
    Collapse,
    Divider,
    Button,
    Tabs,
    Tab,
    Skeleton,
    IconButton,
    Chip,
    Alert,
    TextField,
    InputAdornment,
    Autocomplete,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { useDebounce } from 'use-lodash-debounce';
import { Scrollbar, Iconify } from '../../../components';
import { EmptyContent } from '../../../components/empty-content';
// store
import { useSelector, useDispatch } from '../../../redux/store';
import {
    selectPhoneAndFetchLogs,
    applyFiltersAndFetch,
    getCallLogsForPhone,
    loadMoreCallLogs,
    initializePracticeCallLogs,
    transcribeCall,
    patchCallLead,
    reset as resetCallLogs,
} from '../../../redux/slices/practiceCallLogs';
import { searchLead, clearTaskLead } from '../../../redux/slices/lead';
import { TWILIO_VOICE_CALL_ENDED_EVENT } from '../../twilioVoice/TwilioVoiceProvider';
// style
import { cardStyle } from '../../../styles/AutoMessageAccordianStyle';
import LeadDeskPhoneCalls from './deskCalls/LeadDeskPhoneCalls';
import useAuth from '../../../hooks/useAuth';

// -------------------------------------------------------------------------------------------

const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
};

const STATUS_FILTERS = [
    { value: null, label: 'All', icon: 'solar:list-bold' },
    { value: 'completed', label: 'Completed', icon: 'solar:check-circle-bold', color: '#22c55e' },
    { value: 'busy', label: 'Busy', icon: 'solar:phone-calling-bold', color: '#ef4444' },
    { value: 'no-answer', label: 'Missed', icon: 'solar:phone-bold', color: '#f59e0b' },
];

const formatExactDate = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '';
        return format(date, 'PPpp');
    } catch {
        return '';
    }
};

const getCallDisplayNumber = (log) => {
    if (!log) return '';
    if (log.direction === 'outbound') {
        return log.to_number || log.caller || '';
    }
    return log.from_number || log.caller || log.to_number || '';
};

const getStatusConfig = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('completed') || statusLower.includes('answered')) {
        return { color: '#22c55e', bgColor: '#dcfce7', label: 'completed', icon: 'solar:check-circle-bold' };
    }
    if (statusLower.includes('no-answer') || statusLower.includes('missed')) {
        return { color: '#f59e0b', bgColor: '#fef3c7', label: 'missed', icon: 'solar:phone-bold' };
    }
    if (statusLower.includes('busy')) {
        return { color: '#ef4444', bgColor: '#fee2e2', label: 'busy', icon: 'solar:phone-calling-bold' };
    }
    if (statusLower.includes('failed')) {
        return { color: '#ef4444', bgColor: '#fee2e2', label: 'failed', icon: 'solar:close-circle-bold' };
    }
    if (statusLower.includes('ringing')) {
        return { color: '#3b82f6', bgColor: '#dbeafe', label: 'ringing', icon: 'solar:phone-calling-bold' };
    }
    return { color: '#6b7280', bgColor: '#f3f4f6', label: status || 'unknown', icon: 'solar:phone-bold' };
};

// Skeleton Component for loading states
const CallLogSkeleton = () => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                borderBottom: '1px solid',
                borderColor: alpha(theme.palette.grey[500], 0.12),
            }}
        >
            <Skeleton variant="circular" width={32} height={32} />
            <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={120} height={20} />
                <Skeleton variant="text" width={80} height={14} />
            </Box>
            <Skeleton variant="text" width={60} height={16} />
            <Skeleton variant="rounded" width={70} height={22} />
        </Box>
    );
};

const CallLogItem = ({ log, practiceId, phoneId, isExpanded, onToggle }) => {
    const theme = useTheme();
    const dispatch = useDispatch();

    const { transcriptions } = useSelector((state) => state.practiceCallLogs);
    const { taskSearchLead, isLoading: isLeadSearchLoading } = useSelector((state) => state.lead);
    const transcription = transcriptions[log?.id];
    const isTranscribing = transcription?.isLoading || false;
    // Use stored transcript from API if available, otherwise fall back to Redux transcription state
    const transcriptionData = transcription?.data || log?.transcript || null;
    const transcriptionError = transcription?.error || null;

    const [leadSearchQuery, setLeadSearchQuery] = useState('');
    const [isAssigning, setIsAssigning] = useState(false);
    const debouncedLeadSearch = useDebounce(leadSearchQuery, 500);

    const statusConfig = getStatusConfig(log?.status);

    // Determine if call is inbound or outbound based on direction field or caller
    const isInbound = log?.direction === 'inbound' || !log?.direction;
    const displayNumber = getCallDisplayNumber(log);

    const handleTranscribe = async () => {
        if (!log?.recording_audio_url || !log?.id) return;
        dispatch(transcribeCall(practiceId, phoneId, log.id));
    };

    // Search leads for assignment
    useEffect(() => {
        if (debouncedLeadSearch && debouncedLeadSearch.length >= 2 && practiceId) {
            dispatch(searchLead(debouncedLeadSearch, practiceId, 'taskLead'));
        }
    }, [debouncedLeadSearch, practiceId]);

    const handleAssignLead = async (event, selectedLead) => {
        if (!selectedLead || !log?.id) return;
        setIsAssigning(true);
        const leadName = `${selectedLead.first_name || ''} ${selectedLead.last_name || ''}`.trim();
        await dispatch(patchCallLead(practiceId, phoneId, log.id, selectedLead.id, leadName));
        setIsAssigning(false);
        setLeadSearchQuery('');
        dispatch(clearTaskLead());
    };

    const handleRemoveLead = async () => {
        if (!log?.id) return;
        setIsAssigning(true);
        await dispatch(patchCallLead(practiceId, phoneId, log.id, null, null));
        setIsAssigning(false);
    };

    return (
        <>
            <Box
                onClick={onToggle}
                sx={{
                    py: 1.5,
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    cursor: 'pointer',
                    borderBottom: '1px solid',
                    borderColor: alpha(theme.palette.grey[500], 0.08),
                    transition: 'all 0.15s ease',
                    bgcolor: isExpanded ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                    '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                }}
            >
                {/* Call Direction Icon */}
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isInbound
                            ? alpha(theme.palette.success.main, 0.1)
                            : alpha(theme.palette.info.main, 0.1),
                    }}
                >
                    <Iconify
                        icon={isInbound ? 'solar:incoming-call-rounded-bold' : 'solar:outgoing-call-rounded-bold'}
                        sx={{
                            color: isInbound ? theme.palette.success.main : theme.palette.info.main,
                            width: 18,
                            height: 18
                        }}
                    />
                </Box>

                {/* Phone Number */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.875rem',
                        }}
                    >
                        {displayNumber || 'Unknown'}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                            {isInbound ? 'Inbound' : 'Outbound'}
                        </Typography>
                        {log?.recording_audio_url && (
                            <>
                                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: theme.palette.grey[400] }} />
                                <Iconify
                                    icon="solar:microphone-bold"
                                    sx={{ width: 12, height: 12, color: theme.palette.success.main }}
                                />
                            </>
                        )}
                        {log?.from_country && (
                            <>
                                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: theme.palette.grey[400] }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                    {log.from_country}
                                </Typography>
                            </>
                        )}
                    </Stack>
                </Box>

                {/* Duration - Fixed width for alignment */}
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        width: 50,
                        textAlign: 'right',
                        fontSize: '0.8rem',
                        flexShrink: 0,
                    }}
                >
                    {formatDuration(log?.duration)}
                </Typography>

                {/* Time - Fixed width for alignment */}
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        width: 130,
                        textAlign: 'right',
                        fontSize: '0.8rem',
                        flexShrink: 0,
                    }}
                >
                    {log?.created_at_human || ''}
                </Typography>

                {/* Status Badge - Fixed width for alignment */}
                <Box
                    sx={{
                        px: 1.25,
                        py: 0.375,
                        borderRadius: 0.75,
                        bgcolor: statusConfig.bgColor,
                        width: 80,
                        textAlign: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: statusConfig.color,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            textTransform: 'lowercase',
                        }}
                    >
                        {statusConfig.label}
                    </Typography>
                </Box>

                {/* Expand Icon */}
                <Iconify
                    icon={isExpanded ? 'solar:alt-arrow-up-outline' : 'solar:alt-arrow-down-outline'}
                    sx={{ width: 16, height: 16, color: theme.palette.grey[400], ml: 1, flexShrink: 0 }}
                />
            </Box>

            {/* Expanded Content */}
            <Collapse in={isExpanded}>
                <Box
                    sx={{
                        px: 2,
                        py: 2,
                        bgcolor: alpha(theme.palette.grey[500], 0.02),
                        borderBottom: '1px solid',
                        borderColor: alpha(theme.palette.grey[500], 0.08),
                    }}
                >
                    {/* Audio Player */}
                    {log?.recording_audio_url && (
                        <Box sx={{ mb: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Box
                                    sx={{
                                        flex: 1,
                                        '& audio': {
                                            width: '100%',
                                            height: '40px',
                                            borderRadius: '8px',
                                            '&::-webkit-media-controls-panel': {
                                                bgcolor: alpha(theme.palette.grey[500], 0.08),
                                            },
                                        },
                                    }}
                                >
                                    <audio
                                        controls
                                        preload="metadata"
                                    >
                                        <source src={log.recording_audio_url} type="audio/mpeg" />
                                    </audio>
                                </Box>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleTranscribe}
                                    disabled={isTranscribing || !!transcriptionData}
                                    startIcon={
                                        // eslint-disable-next-line no-nested-ternary
                                        isTranscribing ? (
                                            <CircularProgress size={14} color="inherit" />
                                        ) : transcriptionData ? (
                                            <Iconify icon="solar:check-circle-bold" sx={{ width: 16, height: 16 }} />
                                        ) : (
                                            <Iconify icon="solar:document-text-outline" sx={{ width: 16, height: 16 }} />
                                        )
                                    }
                                    sx={{
                                        minWidth: 100,
                                        height: 32,
                                        fontSize: '0.75rem',
                                        borderColor: transcriptionData
                                            ? alpha(theme.palette.success.main, 0.5)
                                            : alpha(theme.palette.grey[500], 0.32),
                                        color: transcriptionData
                                            ? theme.palette.success.main
                                            : 'text.secondary',
                                    }}
                                >
                                    {/* eslint-disable-next-line no-nested-ternary */}
                                    {isTranscribing ? 'Working...' : transcriptionData ? 'Done' : 'Transcribe'}
                                </Button>
                            </Stack>
                        </Box>
                    )}

                    {/* Transcription Error */}
                    {transcriptionError && (
                        <Alert severity="error" sx={{ mb: 2, py: 0.5 }}>
                            {transcriptionError}
                        </Alert>
                    )}

                    {/* Transcription Content */}
                    {transcriptionData && (
                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: 1,
                                bgcolor: alpha(theme.palette.info.main, 0.04),
                                border: '1px solid',
                                borderColor: alpha(theme.palette.info.main, 0.12),
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
                                <Iconify icon="solar:document-text-bold" sx={{ width: 14, height: 14, color: 'info.main' }} />
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'info.main' }}>
                                    Transcription
                                </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1.6 }}>
                                {typeof transcriptionData === 'string' ? transcriptionData : JSON.stringify(transcriptionData, null, 2)}
                            </Typography>
                        </Box>
                    )}

                    {/* Transcription Loading */}
                    {isTranscribing && !transcriptionData && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Transcribing audio...
                            </Typography>
                        </Box>
                    )}

                    {/* Assign Lead Section */}
                    <Divider sx={{ my: 1.5 }} />
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                            <Iconify icon="solar:user-bold" sx={{ width: 14, height: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                Assigned Lead
                            </Typography>
                        </Stack>
                        {(log?.lead && (typeof log.lead === 'object' ? log.lead.id : log.lead)) ? (
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Chip
                                    label={
                                        log.lead_name ||
                                        (typeof log.lead === 'object'
                                            ? `${log.lead.first_name || ''} ${log.lead.last_name || ''}`.trim() || `Lead #${log.lead.id}`
                                            : `Lead #${log.lead}`)
                                    }
                                    size="small"
                                    icon={<Iconify icon="solar:user-bold" sx={{ width: 14, height: 14 }} />}
                                    sx={{
                                        height: 28,
                                        fontSize: '0.75rem',
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        color: theme.palette.primary.main,
                                        fontWeight: 500,
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={handleRemoveLead}
                                    disabled={isAssigning}
                                    sx={{ width: 24, height: 24 }}
                                >
                                    {isAssigning ? (
                                        <CircularProgress size={12} />
                                    ) : (
                                        <Iconify icon="solar:close-circle-bold" sx={{ width: 16, height: 16, color: 'error.main' }} />
                                    )}
                                </IconButton>
                            </Stack>
                        ) : (
                            <Autocomplete
                                freeSolo
                                size="small"
                                options={taskSearchLead || []}
                                loading={isLeadSearchLoading}
                                inputValue={leadSearchQuery}
                                onInputChange={(e, val, reason) => {
                                    if (reason === 'input') setLeadSearchQuery(val);
                                }}
                                onChange={handleAssignLead}
                                filterOptions={(options) => options}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return `${option?.first_name || ''} ${option?.last_name || ''}`.trim();
                                }}
                                renderOption={(props, option) => (
                                    <Box
                                        component="li"
                                        {...props}
                                        key={option.id}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start !important',
                                            py: '6px !important',
                                            px: '12px !important',
                                            borderBottom: '1px solid',
                                            borderColor: alpha(theme.palette.grey[500], 0.08),
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                                            {option.first_name || ''} {option.last_name || ''}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                                            {option.lead_contact?.email || ''}{option.lead_contact?.phone ? ` | ${option.lead_contact.phone}` : ''}
                                        </Typography>
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search and assign a lead..."
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    {isAssigning ? (
                                                        <CircularProgress size={14} />
                                                    ) : (
                                                        <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 16, height: 16 }} />
                                                    )}
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                fontSize: '0.8rem',
                                                '& fieldset': { borderColor: alpha(theme.palette.grey[500], 0.2) },
                                            },
                                        }}
                                    />
                                )}
                                sx={{ maxWidth: 320 }}
                            />
                        )}
                    </Box>
                </Box>
            </Collapse>
        </>
    );
};

CallLogItem.propTypes = {
    log: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    phoneId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isExpanded: PropTypes.bool,
    onToggle: PropTypes.func,
};

const CallLogs = ({ leadDetail }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const scrollRef = useRef(null);
    const prevPracticeRef = useRef(null);
    const [expandedId, setExpandedId] = useState(null);
    const [showDateFilter, setShowDateFilter] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [tempStartDate, setTempStartDate] = useState(null);
    const [tempEndDate, setTempEndDate] = useState(null);
    // Caller search filter
    const [callerSearch, setCallerSearch] = useState('');
    // Elevate desk-phone calls shown for a lead (above the Twilio history).
    // Keyed by lead so the previous lead's count can't hide this lead's notice.
    // Set only once the lead-calls request has settled.
    const [deskCalls, setDeskCalls] = useState({ leadId: null, count: 0, connected: false });
    const { user } = useAuth();
    // Only DentalSEM sets up Twilio numbers, so only they get told to.
    const isSuperuser = Boolean(user?.is_superuser);

    const debouncedCallerSearch = useDebounce(callerSearch, 500);

    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const {
        callLogs,
        isLoading,
        isLoadingMore,
        callLogsCount,
        twilioPhones,
        phonesStatus,
        selectedPhoneId,
        hasMore,
        filters,
    } = useSelector((state) => state.practiceCallLogs);

    const leadId = leadDetail?.id;
    const deskSettled = deskCalls.leadId === leadId;
    const deskCallCount = deskSettled ? deskCalls.count : 0;
    const deskPhoneConnected = deskSettled && deskCalls.connected;
    const onDeskCallsLoaded = useCallback((count, info) => {
        const connected = info?.deskPhoneConnected === true;
        setDeskCalls((prev) => (
            prev.leadId === leadId && prev.count === count && prev.connected === connected ? prev : { leadId, count, connected }
        ));
    }, [leadId]);
    const hasActiveFilters = filters.startDate || filters.endDate;
    const hasAdvancedFilters = !!filters.callerSearch;

    // Initialize call logs when component mounts or practice changes
    useEffect(() => {
        if (practiceId && leadId) {
            // If practice changed, reset state first
            if (prevPracticeRef.current && prevPracticeRef.current !== practiceId) {
                dispatch(resetCallLogs());
            }
            prevPracticeRef.current = practiceId;
            dispatch(initializePracticeCallLogs(practiceId, leadId));
        }

        // Cleanup on unmount
        return () => {
            dispatch(resetCallLogs());
        };
    }, [practiceId, leadId, dispatch]);

    useEffect(() => {
        const handleCallFinished = (event) => {
            if (!practiceId || !selectedPhoneId || !leadId) return;
            if (event.detail?.practiceId && String(event.detail.practiceId) !== String(practiceId)) return;
            if (event.detail?.phoneId && String(event.detail.phoneId) !== String(selectedPhoneId)) return;
            dispatch(getCallLogsForPhone(practiceId, selectedPhoneId, { ...filters, leadId }));
        };
        window.addEventListener(TWILIO_VOICE_CALL_ENDED_EVENT, handleCallFinished);
        return () => window.removeEventListener(TWILIO_VOICE_CALL_ENDED_EVENT, handleCallFinished);
    }, [dispatch, filters, leadId, practiceId, selectedPhoneId]);

    // Handle phone tab change
    const handlePhoneChange = (event, newPhoneId) => {
        if (newPhoneId !== selectedPhoneId) {
            setExpandedId(null);
            dispatch(selectPhoneAndFetchLogs(practiceId, newPhoneId, { ...filters, leadId }));
        }
    };

    // Handle status filter change
    const handleStatusFilter = (status) => {
        setExpandedId(null);
        dispatch(applyFiltersAndFetch(practiceId, selectedPhoneId, { ...filters, status }));
    };

    // Handle date filter apply
    const handleDateFilterApply = () => {
        setExpandedId(null);
        dispatch(applyFiltersAndFetch(practiceId, selectedPhoneId, {
            startDate: tempStartDate ? format(tempStartDate, 'yyyy-MM-dd') : null,
            endDate: tempEndDate ? format(tempEndDate, 'yyyy-MM-dd') : null,
        }));
        setShowDateFilter(false);
    };

    // Handle clear date filter
    const handleClearDateFilter = () => {
        setTempStartDate(null);
        setTempEndDate(null);
        dispatch(applyFiltersAndFetch(practiceId, selectedPhoneId, {
            startDate: null,
            endDate: null,
        }));
        setShowDateFilter(false);
    };

    // Debounced caller search effect
    useEffect(() => {
        if (debouncedCallerSearch !== undefined && selectedPhoneId) {
            setExpandedId(null);
            dispatch(applyFiltersAndFetch(practiceId, selectedPhoneId, {
                ...filters,
                callerSearch: debouncedCallerSearch || '',
            }));
        }
    }, [debouncedCallerSearch]);

    // Handle clear caller search filter
    const handleClearAdvancedFilters = () => {
        setCallerSearch('');
        setExpandedId(null);
        dispatch(applyFiltersAndFetch(practiceId, selectedPhoneId, {
            ...filters,
            callerSearch: '',
        }));
    };

    // Handle infinite scroll
    const handleScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const threshold = 100;

        if (scrollHeight - scrollTop - clientHeight < threshold && hasMore && !isLoadingMore && selectedPhoneId) {
            dispatch(loadMoreCallLogs(practiceId, selectedPhoneId));
        }
    }, [hasMore, isLoadingMore, selectedPhoneId, practiceId, dispatch]);

    // Desk-phone calls sit first in both branches so the section never remounts
    // (and refetches) when the Twilio phones finish loading.
    const deskCallsSection = <LeadDeskPhoneCalls practiceId={practiceId} leadId={leadId} onLoaded={onDeskCallsLoaded} />;

    // No Twilio phones (CU-869fnwcr5): a practice on a desk phone system
    // (Elevate) gets its own "Desk phone calls" section, empty or not, and no
    // Twilio wording. Otherwise:
    //  - nothing until BOTH the Twilio phones and the desk calls have settled
    //    (no notice flashing while the desk calls load);
    //  - a failed phones request says so, never "none configured";
    //  - the Twilio setup notice only for DentalSEM superusers, who set it up;
    //    everyone else sees "No calls yet".
    if (!isLoading && twilioPhones.length === 0) {
        const phonesSettled = phonesStatus === 'loaded' || phonesStatus === 'error';
        const showNotice = phonesSettled && deskSettled && deskCallCount === 0 && !deskPhoneConnected;
        let notice = null;
        if (showNotice && phonesStatus === 'error') {
            notice = (
                <Alert
                    severity="warning"
                    data-testid="lead-call-logs-phones-error"
                    action={<Button color="inherit" size="small" onClick={() => dispatch(initializePracticeCallLogs(practiceId, leadId))}>Retry</Button>}
                >
                    Call history could not be loaded.
                </Alert>
            );
        } else if (showNotice) {
            notice = (
                <Card sx={{ ...cardStyle, boxShadow: 'none', bgcolor: 'transparent' }}>
                    <EmptyContent
                        title={isSuperuser ? 'No phone numbers configured' : 'No calls yet'}
                        description={isSuperuser ? 'Configure Twilio phone numbers to view call logs' : 'Calls with this patient will show here.'}
                        imgUrl="/assets/icons/empty/ic_email_selected.svg"
                        sx={{ borderRadius: 2, bgcolor: 'background.default', py: 6 }}
                    />
                </Card>
            );
        }
        return (
            <>
                {deskCallsSection}
                {notice}
            </>
        );
    }

    return (
        <>
        {deskCallsSection}
        <Card sx={{ ...cardStyle, boxShadow: 'none', bgcolor: 'transparent', p: 0 }}>
            {/* Header */}
            <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                            }}
                        >
                            <Iconify icon="solar:phone-bold" sx={{ color: 'primary.main', width: 18, height: 18 }} />
                        </Box>
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                Call History
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                {callLogsCount} calls
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={0.75}>
                        {/* Date Filter Toggle */}
                        <Button
                            variant={hasActiveFilters ? "contained" : "outlined"}
                            size="small"
                            onClick={() => setShowDateFilter(!showDateFilter)}
                            startIcon={<Iconify icon="solar:calendar-bold" sx={{ width: 16, height: 16 }} />}
                            sx={{
                                height: 28,
                                fontSize: '0.75rem',
                                ...(!hasActiveFilters && {
                                    color: 'text.secondary',
                                    borderColor: alpha(theme.palette.grey[500], 0.24),
                                }),
                            }}
                        >
                            {hasActiveFilters ? 'Date Filtered' : 'Date'}
                        </Button>

                        {/* Advanced Filters Toggle */}
                        <Button
                            variant={hasAdvancedFilters ? "contained" : "outlined"}
                            size="small"
                            onClick={() => setShowFilters(!showFilters)}
                            startIcon={<Iconify icon="solar:filter-bold" sx={{ width: 16, height: 16 }} />}
                            sx={{
                                height: 28,
                                fontSize: '0.75rem',
                                ...(!hasAdvancedFilters && {
                                    color: 'text.secondary',
                                    borderColor: alpha(theme.palette.grey[500], 0.24),
                                }),
                            }}
                        >
                            {hasAdvancedFilters ? 'Filtered' : 'Filters'}
                        </Button>
                    </Stack>
                </Stack>

                {/* Date Filter Panel */}
                <Collapse in={showDateFilter}>
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: alpha(theme.palette.grey[500], 0.04), borderRadius: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <DatePicker
                                    label="From"
                                    value={tempStartDate}
                                    onChange={setTempStartDate}
                                    slotProps={{ textField: { size: 'small', sx: { width: 140 } } }}
                                />
                                <DatePicker
                                    label="To"
                                    value={tempEndDate}
                                    onChange={setTempEndDate}
                                    minDate={tempStartDate}
                                    slotProps={{ textField: { size: 'small', sx: { width: 140 } } }}
                                />
                                <Button variant="contained" size="small" onClick={handleDateFilterApply} sx={{ height: 36 }}>
                                    Apply
                                </Button>
                                {hasActiveFilters && (
                                    <Button variant="outlined" size="small" onClick={handleClearDateFilter} sx={{ height: 36 }}>
                                        Clear
                                    </Button>
                                )}
                            </Stack>
                        </LocalizationProvider>
                    </Box>
                </Collapse>

                {/* Advanced Filters Panel */}
                <Collapse in={showFilters}>
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: alpha(theme.palette.grey[500], 0.04), borderRadius: 1 }}>
                        <Stack spacing={1.5}>
                            {/* Caller Search */}
                            <TextField
                                size="small"
                                placeholder="Search by caller number..."
                                value={callerSearch}
                                onChange={(e) => setCallerSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 16, height: 16 }} />
                                        </InputAdornment>
                                    ),
                                    ...(callerSearch && {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setCallerSearch('')}>
                                                    <Iconify icon="solar:close-circle-bold" sx={{ width: 16, height: 16 }} />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: '0.8rem',
                                        '& fieldset': { borderColor: alpha(theme.palette.grey[500], 0.2) },
                                    },
                                }}
                            />

                            {/* Clear All Filters */}
                            {hasAdvancedFilters && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleClearAdvancedFilters}
                                    startIcon={<Iconify icon="solar:eraser-bold" sx={{ width: 14, height: 14 }} />}
                                    sx={{
                                        alignSelf: 'flex-start',
                                        height: 28,
                                        fontSize: '0.7rem',
                                        color: 'text.secondary',
                                        borderColor: alpha(theme.palette.grey[500], 0.24),
                                    }}
                                >
                                    Clear Filters
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </Collapse>
            </Box>

            {/* Phone Number Tabs */}
            {twilioPhones.length > 0 && (
                <Box sx={{ px: 2, pb: 1.5 }}>
                    <Tabs
                        value={selectedPhoneId ?? (twilioPhones[0]?.id || false)}
                        onChange={handlePhoneChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 36,
                            '& .MuiTabs-indicator': { height: 2 },
                            '& .MuiTab-root': {
                                minHeight: 36,
                                minWidth: 'auto',
                                px: 1.5,
                                py: 0.5,
                                mr: 1,
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                color: 'text.secondary',
                                '&.Mui-selected': {
                                    color: 'primary.main',
                                    fontWeight: 600,
                                },
                            },
                        }}
                    >
                        {twilioPhones.map((phone) => (
                            <Tab
                                key={phone.id}
                                value={phone.id}
                                icon={<Iconify icon="solar:phone-bold" sx={{ width: 14, height: 14, mr: 0.5 }} />}
                                iconPosition="start"
                                label={phone.phone_number || `Phone ${phone.id}`}
                            />
                        ))}
                    </Tabs>
                </Box>
            )}

            {/* Quick Status Filters */}
            <Box sx={{ px: 2, pb: 1.5 }}>
                <Stack direction="row" spacing={0.75}>
                    {STATUS_FILTERS.map((sf) => (
                        <Chip
                            key={sf.value || 'all'}
                            label={sf.label}
                            size="small"
                            icon={<Iconify icon={sf.icon} sx={{ width: 14, height: 14 }} />}
                            onClick={() => handleStatusFilter(sf.value)}
                            sx={{
                                height: 28,
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                borderRadius: 1,
                                // eslint-disable-next-line no-nested-ternary
                                bgcolor: filters.status === sf.value
                                    ? (sf.color ? alpha(sf.color, 0.15) : alpha(theme.palette.primary.main, 0.15))
                                    : alpha(theme.palette.grey[500], 0.08),
                                color: filters.status === sf.value
                                    ? (sf.color || theme.palette.primary.main)
                                    : 'text.secondary',
                                border: '1px solid',
                                // eslint-disable-next-line no-nested-ternary
                                borderColor: filters.status === sf.value
                                    ? (sf.color ? alpha(sf.color, 0.3) : alpha(theme.palette.primary.main, 0.3))
                                    : 'transparent',
                                '&:hover': {
                                    bgcolor: sf.color ? alpha(sf.color, 0.1) : alpha(theme.palette.primary.main, 0.1),
                                },
                                '& .MuiChip-icon': {
                                    color: 'inherit',
                                },
                            }}
                        />
                    ))}
                </Stack>
            </Box>

            {/* Call Logs List */}
            <Box
                sx={{
                    borderTop: '1px solid',
                    borderColor: alpha(theme.palette.grey[500], 0.12),
                    bgcolor: 'background.paper',
                    borderRadius: '0 0 12px 12px',
                }}
            >
                <Scrollbar
                    ref={scrollRef}
                    sx={{ maxHeight: '480px' }}
                    onScroll={handleScroll}
                >
                    {/* Initial Loading State */}
                    {isLoading && callLogs.length === 0 && (
                        <>
                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                <CallLogSkeleton key={i} />
                            ))}
                        </>
                    )}

                    {/* Empty State */}
                    {!isLoading && callLogs.length === 0 && (
                        <Box sx={{ py: 6 }}>
                            <EmptyContent
                                title="No calls found"
                                description={filters.status ? `No ${filters.status} calls` : "No calls for this lead"}
                                imgUrl="/assets/icons/empty/ic_email_selected.svg"
                                sx={{ bgcolor: 'transparent' }}
                            />
                        </Box>
                    )}

                    {/* Call Log Items */}
                    {callLogs.length > 0 && callLogs.map((log) => (
                        <CallLogItem
                            key={log?.id}
                            log={log}
                            practiceId={practiceId}
                            phoneId={selectedPhoneId}
                            isExpanded={expandedId === log?.id}
                            onToggle={() => setExpandedId(expandedId === log?.id ? null : log?.id)}
                        />
                    ))}

                    {/* Loading More State */}
                    {isLoadingMore && (
                        <>
                            <CallLogSkeleton />
                            <CallLogSkeleton />
                        </>
                    )}

                    {/* End of List */}
                    {!hasMore && callLogs.length > 0 && (
                        <Box sx={{ py: 2, textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                End of calls
                            </Typography>
                        </Box>
                    )}
                </Scrollbar>
            </Box>
        </Card>
        </>
    );
};

CallLogs.propTypes = {
    leadDetail: PropTypes.object,
};

CallLogs.defaultProps = {
    leadDetail: {},
};

export default CallLogs;
